import AddCardOutlinedIcon from '@mui/icons-material/AddCardOutlined';
import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { formatCardLabel } from '~/utils/creditCard';
import CardBrandIcon from './CardBrandIcon';
import { NEW_CARD_OPTION, type JourneySavedCard } from './types';

type Props = {
  appId: string;
  cards: JourneySavedCard[];
  /** Id do cartão escolhido ou `'new'` para «Novo cartão de crédito». */
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Formulário do cartão novo; aparece abaixo da opção quando ela está selecionada. */
  newCardForm?: ReactNode;
  /**
   * `select` (renovação): salvos selecionáveis. `readonly`/`hidden` (troca de cartão): salvos só
   * informativos ou ocultos — decisão em CORE-988.
   */
  savedCardsMode?: 'select' | 'readonly' | 'hidden';
  disabled?: boolean;
};

const optionSx = (selected: boolean) => (theme: { customColors: { backgroundTertiary: string } }) => ({
  bgcolor: selected ? theme.customColors.backgroundTertiary : 'background.paper',
  border: '1px solid',
  borderColor: selected ? 'primary.light' : 'divider',
  borderRadius: '8px',
  m: 0,
  minHeight: 56,
  px: '8px',
  width: '100%'
});

function OptionLabel({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <Box sx={{ alignItems: 'center', display: 'flex', gap: '8px', minWidth: 0 }}>
      {icon}
      <Typography noWrap sx={{ color: 'text.primary' }} variant="body2">
        {text}
      </Typography>
    </Box>
  );
}

/**
 * Forma de pagamento da jornada 2026 (Figma 2091:3730, 2091:4107, 2091:4335): cartões salvos com bandeira
 * e final e «Novo cartão de crédito», num `RadioGroup` em cards. Compartilhado por renovação e troca de cartão.
 */
export default function JourneyPaymentMethodSelector({
  appId,
  cards,
  selectedId,
  onSelect,
  newCardForm,
  savedCardsMode = 'select',
  disabled = false
}: Props) {
  const newSelected = selectedId === NEW_CARD_OPTION;

  return (
    <Box data-app-id={appId} sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
      {savedCardsMode === 'readonly' && cards.length > 0 ? (
        <Box data-app-id={`${appId}-saved-readonly`} sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {cards.map((card) => (
            <Box
              key={card.id}
              sx={(theme) => ({ ...optionSx(false)(theme), alignItems: 'center', display: 'flex', p: '8px' })}
            >
              <OptionLabel
                icon={<CardBrandIcon brand={card.brand} />}
                text={formatCardLabel(card.brand, card.lastFour)}
              />
            </Box>
          ))}
        </Box>
      ) : null}

      <RadioGroup
        aria-label="Forma de pagamento"
        onChange={(event) => onSelect(event.target.value)}
        sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
        value={selectedId ?? ''}
      >
        {savedCardsMode === 'select'
          ? cards.map((card) => {
              const label = formatCardLabel(card.brand, card.lastFour);
              return (
                <FormControlLabel
                  control={<Radio inputProps={{ 'aria-label': label }} />}
                  data-app-id={`${appId}-card-${card.id}`}
                  disabled={disabled}
                  key={card.id}
                  label={<OptionLabel icon={<CardBrandIcon brand={card.brand} />} text={label} />}
                  sx={optionSx(selectedId === card.id)}
                  value={card.id}
                />
              );
            })
          : null}
        <FormControlLabel
          control={<Radio inputProps={{ 'aria-label': 'Novo cartão de crédito' }} />}
          data-app-id={`${appId}-new-card`}
          disabled={disabled}
          label={
            <OptionLabel
              icon={
                <Box
                  aria-hidden
                  sx={{
                    alignItems: 'center',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '100px',
                    display: 'flex',
                    height: 40,
                    justifyContent: 'center',
                    width: 40
                  }}
                >
                  <AddCardOutlinedIcon sx={{ color: 'text.primary', fontSize: 22 }} />
                </Box>
              }
              text="Novo cartão de crédito"
            />
          }
          sx={optionSx(newSelected)}
          value={NEW_CARD_OPTION}
        />
      </RadioGroup>

      {newSelected && newCardForm ? newCardForm : null}
    </Box>
  );
}
